import {
  type ExternalLink,
  selectTabExternalLinks,
} from '@octopus-do/octopus-project-core'
import { migrationCanvasSelectors } from '@store/migrationSelectors/canvasSelectors'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import cx from 'classnames'
import type React from 'react'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useArtboardMode, useEventListener } from '@/hooks'
import useWidth from '@/hooks/useWidth'
import { COMPACT_LAYOUT_UPDATED } from '../CompactLayout'
import css from './Arrows.module.scss'
import { ArrowLine } from './components/ArrowLine'
import { ArrowSmooth } from './components/ArrowSmooth'
import { SelectedArrowToolbar } from './components/SelectedArrowToolbar'
import { useGetArrows } from './hooks/useGetArrows'

const Arrows: React.FC = () => {
  // Compact positions cards after render; the anchors re-read their rects.
  const [, setLayoutRevision] = useState(0)
  useEventListener(COMPACT_LAYOUT_UPDATED, () =>
    setLayoutRevision((revision) => revision + 1)
  )

  const activeTab = useSelector(migrationProjectSelector.getActiveOrFirstTabId)

  const documentRef = useRef<Document>(document)
  const dispatch = useDispatch()

  const {
    simpleView: isHidden,
    arrows: isArrowMode,
    gallery,
    seoMode,
  } = useArtboardMode()

  // hack to rerender arrow positions
  // @TODO find a way another solution
  useWidth(20)
  useSelector(migrationProjectSelector.getProject)
  useSelector(migrationProjectSelector.getProjectLegacyTree)
  useSelector(migrationProjectSelector.getProjectSections)
  useSelector(migrationCanvasSelectors.getCanvasExternalLinkOffset)
  useSelector(migrationCanvasSelectors.getScale)

  useSelector<AppState, ExternalLink[]>((state) =>
    selectTabExternalLinks(activeTab)({
      project: state.manager.project,
      relations: state.relations,
    })
  )

  const arrowsHide = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.arrow.hideArrows
  )

  const arrows = useGetArrows()
  const selectedArrow = arrows.find(({ isSelected }) => isSelected)

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const classes = cx(css.container, 'Tree-arrows', {
    [css.readonly]: isReadOnly,
    [css.hide]: arrowsHide,
  })

  const onOutsideClick = () => {
    if (isArrowMode) {
      dispatch(artBoardActions.changeMode('edit'))
    }
  }

  // @todo review how to implement with global
  useEventListener('pointerdown', onOutsideClick, documentRef)

  if (isHidden) {
    return null
  }

  // hack
  // We need Math.random() as key here to force rerender xarrows component
  // @TODO find a better solution
  return (
    <div className={classes}>
      {arrows.map((arrow) =>
        arrow.type === 'smooth' ? (
          <ArrowSmooth key={Math.random()} {...arrow} />
        ) : (
          <ArrowLine key={Math.random()} {...arrow} />
        )
      )}
      {selectedArrow && !isReadOnly && !arrowsHide && !(gallery && seoMode) && (
        <SelectedArrowToolbar key={selectedArrow.id} arrow={selectedArrow} />
      )}
    </div>
  )
}

export default Arrows
